import { useCallback, useEffect, useState } from 'react';

import { DividerPosition } from '../ResizeDivider';

export type UseResizePanelProps = {
  open?: boolean;
  width?: number;
  openRight?: boolean;
  onResize?: (width: number) => void;
  minWidth?: number;
  maxWidth?: number;
  resizeAffordance?: number;
  closedWidth?: number;
  storageKey?: string;
};

const getStoredWidth = (storageKey?: string): number | undefined => {
  if (!storageKey) {
    return undefined;
  }

  try {
    const item = globalThis.localStorage.getItem(storageKey);

    if (item === null) {
      return undefined;
    }

    const parsed: unknown = JSON.parse(item);

    if (typeof parsed !== 'number') {
      return undefined;
    }

    return parsed;
  } catch {
    console.error('Failed to read panel width from localStorage');
    return undefined;
  }
};

const setStoredWidth = (
  storageKey: string | undefined,
  value: number,
): void => {
  if (!storageKey) {
    return;
  }
  try {
    globalThis.localStorage.setItem(storageKey, JSON.stringify(value));
  } catch {
    console.error('Failed to persist panel width to localStorage');
  }
};

export default ({
  width,
  minWidth,
  maxWidth,
  openRight,
  onResize,
  open,
  resizeAffordance = 16,
  closedWidth,
  storageKey,
}: UseResizePanelProps) => {
  const [initialDragX, setInitialDragX] = useState(0);
  const [widthBeforeDrag, setWidthBeforeDrag] = useState<number | undefined>(
    () => getStoredWidth(storageKey) ?? width,
  );
  const [panelWidth, setPanelWidth] = useState<number | undefined>(
    widthBeforeDrag,
  );
  const [isResizing, setIsResizing] = useState(false);
  const [maxReached, setMaxReached] = useState(false);
  const [minReached, setMinReached] = useState(false);

  useEffect(() => {
    if (!storageKey) {
      setPanelWidth(width);
      setWidthBeforeDrag(width);
    }
  }, [width, storageKey]);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (isResizing && widthBeforeDrag) {
        e.persist();
        const deltaX = openRight
          ? e.pageX - initialDragX
          : initialDragX - e.pageX;
        const dragAmount = deltaX * window.devicePixelRatio;
        let newWidth = widthBeforeDrag + dragAmount / window.devicePixelRatio;

        if (minWidth && newWidth < minWidth) {
          newWidth = minWidth;
          setMinReached(true);
        } else if (maxWidth && newWidth > maxWidth) {
          newWidth = maxWidth;
          setMaxReached(true);
        } else {
          setMinReached(false);
          setMaxReached(false);
        }

        setPanelWidth(newWidth);

        if (onResize) {
          onResize(newWidth);
        }
      }
    },
    [
      initialDragX,
      isResizing,
      maxWidth,
      minWidth,
      onResize,
      openRight,
      widthBeforeDrag,
    ],
  );

  const startResizing = useCallback(
    (e: React.MouseEvent) => {
      if (!isResizing) {
        e.persist();
        setIsResizing(true);
        setInitialDragX(e.pageX);
      }
    },
    [isResizing],
  );

  const stopResizing = useCallback(
    (e: React.MouseEvent) => {
      if (isResizing) {
        e.persist();
        setIsResizing(false);
        setWidthBeforeDrag(panelWidth);
        setMinReached(false);
        setMaxReached(false);

        if (panelWidth !== undefined) {
          setStoredWidth(storageKey, panelWidth);
        }
      }
    },
    [isResizing, panelWidth, storageKey],
  );

  useEffect(() => {
    if (onResize) {
      if (!open) {
        onResize(closedWidth || 0);
      } else if (panelWidth) {
        onResize(panelWidth);
      } else {
        onResize(0);
      }
    }
  }, [panelWidth, onResize, open, closedWidth]);

  const getResizeDividerProps = useCallback(
    () => ({
      onMouseDown: startResizing,
      onMouseUp: stopResizing,
      onMouseMove: handleMouseMove,
      position: openRight ? DividerPosition.right : DividerPosition.left,
      vertical: true,
      affordanceWidth: resizeAffordance,
      minReached,
      maxReached,
    }),
    [
      handleMouseMove,
      maxReached,
      minReached,
      openRight,
      resizeAffordance,
      startResizing,
      stopResizing,
    ],
  );

  const getPanelProps = useCallback(
    <T>(props: T) => ({
      minWidth: closedWidth,
      open,
      width: !open ? closedWidth : panelWidth,
      disableAnimation: isResizing,
      ...props,
    }),
    [closedWidth, isResizing, open, panelWidth],
  );

  return {
    getResizeDividerProps,
    getPanelProps,
  };
};
